/*
 * @providesModule INTL
 *
 */
import I18nLocales from 'I18nLocales';
import Preferences from 'Preferences';
import moment from 'moment';
import {Platform} from 'react-native';

import englishTranslations from './Locales/en.json';

const _moments = I18nLocales.moments;
let _messages = I18nLocales.messages;

const TEN = 10;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

type CurrencyStringPart = 'group' | 'decimal' | 'currency';

export type CurrencyCode = 'USD' | 'INR';

class Internationalization {
  originalMessageFunction: (key: string, ...params: any[]) => string;

  // locale reported by the device
  private _locale: any;

  private _pluralRules?: Intl.PluralRules;

  private _currencyCode: CurrencyCode = 'USD';

  private _currencyFormat!: Intl.NumberFormat;
  private _currencyNoDecimalFormat!: Intl.NumberFormat;
  private _currencyNoSymbolFormat!: Intl.NumberFormat;
  private _currencyNoSymbolNoDecimalFormat!: Intl.NumberFormat;

  private _integerFormat!: Intl.NumberFormat;
  private _floatFormat!: Intl.NumberFormat;
  private _scoreFormat!: Intl.NumberFormat;
  private _abbreviatedFormat!: Intl.NumberFormat;

  private _decimalSeparator!: string;
  private _groupSeparator!: string;
  private _currencySymbol!: string;

  constructor() {
    this.originalMessageFunction = this.message;
    I18nLocales.updateCurrentMomentForLocale(I18nLocales.currentLocale);
    this._locale = I18nLocales.currentLocale;
    this.setCurrencyAndLocale(this._currencyCode, this._locale);
  }

  private getSeparator = (separatorType: CurrencyStringPart): string => {
    const numberWithGroupAndDecimalSeparator = 10000.1;
    return (
      this._currencyFormat
        ?.formatToParts?.(numberWithGroupAndDecimalSeparator)
        ?.find((part: any): any => part.type === separatorType)?.value ??
      this.getDefaultSeparator(separatorType)
    );
  };

  private getDefaultSeparator(separatorType: CurrencyStringPart): string {
    switch (separatorType) {
      case 'group':
        return ',';
      case 'decimal':
        return '.';
      case 'currency':
        return '$';
    }
  }

  daysHrsMinsSecsUntil(date: string, nowDate: Date = new Date(Date.now())): string {
    const endDate = new Date(date);

    // finds difference in time between the 2 dates in days, hours, minutes, seconds
    // don't use duration library because it is buggy for days
    let seconds = Math.floor((endDate.getTime() - nowDate.getTime()) / MS_PER_SECOND);
    let minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
    let hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const days = Math.floor(hours / HOURS_PER_DAY);

    hours = hours - days * HOURS_PER_DAY;
    minutes = minutes - days * HOURS_PER_DAY * MINUTES_PER_HOUR - hours * MINUTES_PER_HOUR;
    seconds =
      seconds -
      days * HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE -
      hours * MINUTES_PER_HOUR * SECONDS_PER_MINUTE -
      minutes * SECONDS_PER_MINUTE;

    const daysString = (days < TEN ? '0' : '') + days;
    const hrsString = (hours < TEN ? '0' : '') + hours;
    const minString = (minutes < TEN ? '0' : '') + minutes;
    const secString = (seconds < TEN ? '0' : '') + seconds;

    const dateString = `${daysString}:${hrsString}:${minString}:${secString}`;

    return dateString;
  }
  /**
   * Return the time until the end time in hours and minutes. If the time is NOT between
   * the start and end time, it will return "0h 0m". This method assumes endTimeStamp is AFTER
   * the current time. If the given time is 02:00:00+0:00, and the current time is 07:00:00+0:00,
   * the end time is treated as the next day.
   *
   * @param startTimeStamp start time of the window in UTC in the form 23:00:00+0:00. If the current time is before this, return 0h 0m
   * @param endTimeStamp end time to calaculate time until in UTC in the form 23:00:00+0:00
   * @returns string with hours and minutes until endTimeStamp
   */
  getTimeUntilUTC(startTimeStamp: string, endTimeStamp: string): string {
    const zeroTime = '0h 0m';
    if (!startTimeStamp || !endTimeStamp) {
      return zeroTime;
    }
    const currentDateTime = new Date();
    const endUTCDateTime = this.getEndDateFromStartAndEndTime(startTimeStamp, endTimeStamp);
    if (!endUTCDateTime) {
      return zeroTime;
    }
    const msRemaining = endUTCDateTime.getTime() - currentDateTime.getTime();
    if (msRemaining <= 0) {
      return zeroTime;
    }

    const totalMinutesRemaining = Math.floor(msRemaining / (MS_PER_SECOND * SECONDS_PER_MINUTE));
    const minutesRemaining = `${Math.floor(totalMinutesRemaining % MINUTES_PER_HOUR)}`;
    const hoursRemaining = `${Math.floor(totalMinutesRemaining / MINUTES_PER_HOUR)}`;
    return `${hoursRemaining}h ${minutesRemaining}m`;
  }
  /**
   * Return Date Object for end time using start and end time to determine
   * how to make end time the right day to ensure end time is after start time
   *
   * @param startTimeStamp start time of the window in UTC in the form 23:00:00+0:00. If the current time is before this, return 0h 0m
   * @param endTimeStamp end time to calaculate time until in UTC in the form 23:00:00+0:00
   * @returns date object or undefined if invalid start and end time
   */
  getEndDateFromStartAndEndTime(startTimeStamp: string, endTimeStamp: string): Date | undefined {
    const currentDateTime = new Date();
    const currentDay = currentDateTime.getUTCDate();
    const currentMonth = currentDateTime.getUTCMonth();
    const currentYear = currentDateTime.getUTCFullYear();

    // The parsing here requires the date to match the format HH:MM:SS+00:00
    const startUTCDateTime = new Date();
    const startTimeArray = startTimeStamp.split('+')[0].split(':');
    startUTCDateTime.setUTCFullYear(currentYear, currentMonth, currentDay);
    startUTCDateTime.setUTCHours(parseInt(startTimeArray[0]));
    startUTCDateTime.setUTCMinutes(parseInt(startTimeArray[1]));
    startUTCDateTime.setUTCSeconds(parseInt(startTimeArray[2]));

    // The parsing here requires the date to match the format HH:MM:SS+00:00
    const endUTCDateTime = new Date();
    const endTimeArray = endTimeStamp.split('+')[0].split(':');
    endUTCDateTime.setUTCFullYear(currentYear, currentMonth, currentDay);
    endUTCDateTime.setUTCHours(parseInt(endTimeArray[0]));
    endUTCDateTime.setUTCMinutes(parseInt(endTimeArray[1]));
    endUTCDateTime.setUTCSeconds(parseInt(endTimeArray[2]));

    const wrapsMidnight = endUTCDateTime.getTime() < startUTCDateTime.getTime();
    const beforeStart = currentDateTime.getTime() < startUTCDateTime.getTime();
    const afterEnd = currentDateTime.getTime() > endUTCDateTime.getTime();

    // Handle current time being out of bounds
    // Which is either before or after the start/end, or BOTH if the start and end bridge midnight
    if (
      (wrapsMidnight && beforeStart && afterEnd) ||
      (!wrapsMidnight && (beforeStart || afterEnd))
    ) {
      return undefined;
    }

    // We crossed midnight, and the current time is "after" the end time, but in range, so we need to make the end come after
    if (wrapsMidnight && afterEnd) {
      endUTCDateTime.setUTCDate(endUTCDateTime.getUTCDate() + 1);
    }
    return endUTCDateTime;
  }

  getSimplifiedTime(date: string | Date, nowDate: Date = new Date(Date.now())): string {
    const endDate = new Date(date);

    // finds difference in time between the 2 dates in days, hours, minutes, seconds
    // don't use duration library because it is buggy for days

    let seconds = Math.round((endDate.getTime() - nowDate.getTime()) / MS_PER_SECOND);
    let minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
    let hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const days = Math.floor(hours / HOURS_PER_DAY);

    hours = hours - days * HOURS_PER_DAY;
    minutes = minutes - days * HOURS_PER_DAY * MINUTES_PER_HOUR - hours * MINUTES_PER_HOUR;
    seconds =
      seconds -
      days * HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE -
      hours * MINUTES_PER_HOUR * SECONDS_PER_MINUTE -
      minutes * SECONDS_PER_MINUTE;

    let daysString = '';
    let hrsString = '';
    let minString = '';
    let secString = '';
    if (days > 0) {
      daysString = _m('{0}d ', days);
      hrsString = _m('{0}h', hours);
    } else if (hours > 0) {
      hrsString = _m('{0}h ', hours);
      minString = _m('{0}m ', minutes);
      secString = _m('{0}s', seconds);
    } else if (minutes > 0) {
      minString = _m('{0}m ', minutes);
      secString = _m('{0}s', seconds);
    } else {
      secString = _m('{0}s', seconds);
    }
    return `${daysString}${hrsString}${minString}${secString}`;
  }

  getIntegerString(number: number): string {
    if (number !== 0 && !number) {
      // Bad argument, return empty string;
      return '';
    }
    return this._integerFormat.format(number);
  }

  getFloatString(number: number): string {
    if (number !== 0 && !number) {
      // Bad argument, return empty string;
      return '';
    }
    return this._floatFormat.format(number);
  }

  /**
   * If you pass a string formatted in en-US, it will split from the decimal point and reformat with current locale.
   * If you pass a number, it will just format it in the currenct locale.
   *
   * Examples:
   *  1000000.0 will return '1,000,000.0' if your locale is en-US
   *  1000000.0 will return '10,00,000.0' if your locale is en-IN
   *  1000000.0 will return '1.000.000,0' if your locale is pt-BR
   *
   *  '1,000,000.0' will return '1,000,000.0' if your locale is en-US
   *  '1,000,000.0' will return '10,00,000.0' if your locale is en-IN
   *  '1,000,000.0' will return '1.000.000,0' if your locale is pt-BR
   * @param number score to be formatted
   * @returns formatted number in current locale
   */
  getFormattedScore(number: number | string): string {
    if (typeof number == 'string') {
      const parts = number.split('.');
      if (parts && parts.length > 1) {
        const decimalDelimiter = this._decimalSeparator;
        parts[0] = this._scoreFormat.format(Number.parseFloat(parts[0].split(',').join('')));
        return parts.join(decimalDelimiter);
      } else {
        return this._scoreFormat.format(Number.parseInt(number.split(',').join('')));
      }
    } else {
      return this._scoreFormat.format(number);
    }
  }

  currencySymbol(): string {
    return this._currencySymbol;
  }

  currentCurrency(): CurrencyCode {
    return this._currencyCode;
  }

  getCurrencyString(
    number: number,
    prefix: string,
    truncZeroFraction?: boolean,
    truncIfOneHundredPlus?: boolean
  ): string {
    if (number !== 0 && !number) {
      // Bad argument, return empty string;
      return '';
    }

    const shouldAddPlusSignToCurrencySymbol = prefix === '+$';
    const shouldHaveLocaleCurrencySymbol =
      prefix === '$' || prefix === this._currencySymbol || shouldAddPlusSignToCurrencySymbol;

    const ONE_HUNDRED = 100;
    let formatter =
      shouldHaveLocaleCurrencySymbol && Platform.OS === 'android'
        ? this._currencyFormat
        : this._currencyNoSymbolFormat;
    if (truncZeroFraction && !(number % 1)) {
      formatter =
        shouldHaveLocaleCurrencySymbol && Platform.OS === 'android'
          ? this._currencyNoDecimalFormat
          : this._currencyNoSymbolNoDecimalFormat;
    }
    if (truncIfOneHundredPlus && number >= ONE_HUNDRED) {
      formatter =
        shouldHaveLocaleCurrencySymbol && Platform.OS === 'android'
          ? this._currencyNoDecimalFormat
          : this._currencyNoSymbolNoDecimalFormat;
    }

    const platformPrefix =
      Platform.OS === 'ios' || Platform.OS === 'web' ? this.currencySymbol() : '';

    //prefix can be ⓩ or whatever custom Unicode character the game developer defines
    return shouldHaveLocaleCurrencySymbol
      ? shouldAddPlusSignToCurrencySymbol
        ? `+${platformPrefix}${formatter.format(number)}`
        : `${platformPrefix}${formatter.format(number)}`
      : `${prefix}${formatter.format(number)}`;
  }

  abbreviatedNumber(number: number): string {
    if (number !== 0 && !number) {
      return '';
    }

    const significantValue = Math.trunc(number);
    const oneThousand = 1000;

    if (significantValue == 0) {
      return '-';
    } else if (significantValue > -oneThousand && significantValue < oneThousand) {
      return significantValue.toString();
    }

    if (this._locale == 'en-IN') {
      return this.abbreviatedIndianEnglish(significantValue);
    }

    return this.abbreviatedDefault(significantValue);
  }

  private abbreviatedIndianEnglish(number: number): string {
    const lakhCroreSuffix = 'L Cr';
    const croreSuffix = 'Cr';
    const lakhSuffix = 'L';
    const thousandSuffix = 'K';

    let significantValue = number;

    const oneLakhCrore = 1000000000000; //10,00,00,00,00,000
    const oneCrore = 10000000; //1,00,00,000
    const oneLakh = 100000; //1,00,000
    const oneThousand = 1000; //1,000

    let suffix = '';
    if (significantValue >= oneLakhCrore || significantValue <= -oneLakhCrore) {
      significantValue = significantValue / oneLakhCrore;
      suffix = lakhCroreSuffix;
    } else if (significantValue >= oneCrore || significantValue <= -oneCrore) {
      significantValue = significantValue / oneCrore;
      suffix = croreSuffix;
    } else if (significantValue >= oneLakh || significantValue <= -oneLakh) {
      significantValue = significantValue / oneLakh;
      suffix = lakhSuffix;
    } else if (significantValue >= oneThousand || significantValue <= -oneThousand) {
      significantValue = significantValue / oneThousand;
      suffix = thousandSuffix;
    }

    return this.formatAbbreviatedResult(significantValue, suffix);
  }

  private abbreviatedDefault(number: number): string {
    const billionSuffix = 'B';
    const millionSuffix = 'M';
    const thousandSuffix = 'K';

    let significantValue = number;

    const oneBillion = 1000000000;
    const oneMillion = 1000000;
    const oneThousand = 1000;

    let suffix = '';
    if (significantValue >= oneBillion || significantValue <= -oneBillion) {
      significantValue = significantValue / oneBillion;
      suffix = billionSuffix;
    } else if (significantValue >= oneMillion || significantValue <= -oneMillion) {
      significantValue = significantValue / oneMillion;
      suffix = millionSuffix;
    } else if (significantValue >= oneThousand || significantValue <= -oneThousand) {
      significantValue = significantValue / oneThousand;
      suffix = thousandSuffix;
    }

    return this.formatAbbreviatedResult(significantValue, suffix);
  }

  private formatAbbreviatedResult(significantValue: number, suffix: string): string {
    let wasNegative = false;

    if (significantValue < 0) {
      wasNegative = true;
      significantValue = Math.abs(significantValue);
    }
    let result = this._abbreviatedFormat.format(significantValue);

    if (result.length > 4) {
      const decimalSeparator = '.';

      const indexOfDecimalSeparator = result.indexOf(decimalSeparator);

      if (indexOfDecimalSeparator >= 0) {
        if (result.substr(0, indexOfDecimalSeparator).length < 3) {
          // We want to truncate after the first digit after the decimal separator.
          result = result.substring(0, indexOfDecimalSeparator + 2);
        } else {
          result = result.substring(0, indexOfDecimalSeparator);
        }
      }
    }

    result = result.replace(/(\.0*)$/, '', 'g');
    return (wasNegative && `-${result}${suffix}`) || `${result}${suffix}`;
  }

  getOrdinalString(number: number): string {
    if (number == undefined) {
      return '';
    }
    const indicator = this.getOrdinalSuffix(number);
    return I18nLocales.currentMoment === 'zh' || I18nLocales.currentMoment === 'zh-hans'
      ? `${indicator}${number}`
      : `${number}${indicator}`;
  }

  getOrdinalSuffix(number: number): string {
    if (number == undefined) {
      return '';
    }
    const ordinals = I18nLocales.ordinals[I18nLocales.currentMoment] ?? I18nLocales.ordinals['en'];

    if (!this._pluralRules) {
      return this._getOrdinalSuffixCrappyPolyfill(ordinals, number);
    }

    return ordinals[this._pluralRules.select(number)];
  }

  private _getOrdinalSuffixCrappyPolyfill(ordinals: any, number: number): string {
    if (number !== Math.trunc(number)) {
      return ordinals.other;
    }
    const intString = Math.trunc(number).toString();
    switch (intString[intString.length - 1]) {
      case '0':
        return ordinals.zero;
      case '1':
        return ordinals.one;
      case '2':
        return ordinals.two;
      case '3':
        return ordinals.few; // this isn't perfect, but it'll do for now
      default:
        return ordinals.many;
    }
  }

  message = (key: string, ...params: any[]): string => {
    if (!key) {
      return '';
    }
    if (params.length) {
      return this.formattedMessage.apply(this, [key, ...params]);
    }

    let i18nMessage = this.messages()[key];

    if (!i18nMessage) {
      i18nMessage = key.toString();
    }

    return i18nMessage;
  };

  formattedMessage(key: string, ...values: any[]): string {
    let formattedStr = this.message(key);

    for (let i = 0; i < values.length; i++) {
      formattedStr = formattedStr.replace(`{${i}}`, values[i]);
    }
    return formattedStr;
  }

  timeFromNow(date: string, omitSuffix: boolean = false): string {
    return moment(date).fromNow(omitSuffix);
  }

  formattedDate(date: string): string {
    return moment(date).format('l');
  }

  formattedTimeStamp(date: Date): string {
    return moment(date).format('LT');
  }

  formattedTime(date: string): string {
    const offset = moment(date).utcOffset();
    const sign = offset > 0 ? '+' : '';

    return `${moment(date).format('h:mm a')} GMT${sign}${Math.floor(offset / MINUTES_PER_HOUR)}`;
  }

  humanReadableInterval(seconds: number, shouldCapitalizeAllWords: boolean): string {
    let str = moment().add(seconds, 'seconds').fromNow(true);

    if (shouldCapitalizeAllWords) {
      str = str.replace(/\w\S*/g, (txt: string): string => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    } else {
      str = str.charAt(0).toUpperCase() + str.slice(1);
    }
    return str;
  }

  humanReadableIntervalAsInteger(seconds: number): string {
    const now = moment();
    const endTime = moment().add(seconds, 'seconds');
    const hours = endTime.diff(now, 'hours');
    const minutes = endTime.diff(now, 'minutes');
    const minsRemaining = minutes - hours * MINUTES_PER_HOUR;

    let str = '';
    if (hours > 1) {
      str = _m('{0} hours', hours);
      if (minsRemaining > 0) {
        str = str + ' ' + _m('{0} minutes', minsRemaining);
      }
      return str;
    } else {
      return _m('{0} minutes', minutes);
    }
  }

  defaultErrorMessage(): string {
    return _m("That doesn't seem like it worked. Please try again.");
  }

  getDeviceLocale(): string {
    return I18nLocales.deviceLocale;
  }

  setCurrencyAndLocale(currency: CurrencyCode, locale?: string) {
    this._currencyCode = currency;
    const actualLocale = locale ?? I18nLocales.currentLocale;
    this._setLocale(actualLocale);
  }

  private _setLocale(newLocale: string) {
    if (!newLocale) {
      return;
    }

    this._locale = newLocale;
    const defaultCurrencyOptions = {
      style: 'currency',
      currency: this._currencyCode,
      currencyDisplay: Platform.OS === 'ios' ? 'symbol' : 'narrowSymbol',
    };

    this._currencyFormat = new Intl.NumberFormat(this._locale, defaultCurrencyOptions);

    this._decimalSeparator = this.getSeparator('decimal');
    this._groupSeparator = this.getSeparator('group');
    this._currencySymbol = this.getSeparator('currency');
    if (this._currencySymbol == 'US$') {
      // if locale is en-IN and currency is USD, Intl.NumberFormat will return US$ for symbol
      this._currencySymbol = '$';
    }
    // console.debug(`Resolved currency symbol is "${this._currencySymbol}"`);
    this._currencyNoDecimalFormat = new Intl.NumberFormat(this._locale, {
      ...defaultCurrencyOptions,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

    const {minimumIntegerDigits, minimumFractionDigits, maximumFractionDigits} =
      new Intl.NumberFormat(this._locale, {
        style: 'currency',
        currency: this._currencyCode,
      }).resolvedOptions();

    this._currencyNoSymbolFormat = new Intl.NumberFormat(this._locale, {
      minimumIntegerDigits,
      minimumFractionDigits,
      maximumFractionDigits,
    });
    this._currencyNoSymbolNoDecimalFormat = new Intl.NumberFormat(this._locale, {
      minimumIntegerDigits,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

    this._integerFormat = new Intl.NumberFormat(this._locale, {maximumFractionDigits: 0});
    this._floatFormat = new Intl.NumberFormat(this._locale, {minimumFractionDigits: 2});
    this._scoreFormat = new Intl.NumberFormat(this._locale, {maximumFractionDigits: 5});
    this._abbreviatedFormat = new Intl.NumberFormat(this._locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: false,
    });

    if (Intl.PluralRules) {
      // PluralRules doesn't exist on ios 12 and below
      this._pluralRules = new Intl.PluralRules(this._locale, {type: 'ordinal'});
    }

    I18nLocales.updateCurrentMomentForLocale(newLocale.toLowerCase());
  }

  locale(): string {
    return this._locale;
  }

  setMessages(newMessages: string[]) {
    if (!newMessages) {
      return;
    }

    _messages = newMessages;
  }

  messages(locale: string = I18nLocales.currentMoment): string[] {
    return _messages[locale] || {};
  }

  _m(key: string, ...str: any[]): string {
    return this.message(key, ...str);
  }

  enablePsueodLocalization(flag: boolean) {
    if (flag && !this.originalMessageFunction) {
      this.originalMessageFunction = this.message;

      this.message = (key: string, ...str: object[]): string => {
        const result = this.originalMessageFunction(key, ...str);

        return `${result} ${result}`;
      };
    } else if (!flag && this.originalMessageFunction) {
      this.message = this.originalMessageFunction;
      delete this.originalMessageFunction;
    }
  }

  _getTimeRemainingFromSeconds(secondsRemaining: number): string {
    let minutes = Math.floor(secondsRemaining / SECONDS_PER_MINUTE);
    const hours = Math.floor(minutes / MINUTES_PER_HOUR);

    minutes = minutes - hours * MINUTES_PER_HOUR;
    const seconds =
      secondsRemaining -
      hours * MINUTES_PER_HOUR * SECONDS_PER_MINUTE -
      minutes * SECONDS_PER_MINUTE;

    const hrsString = (hours < TEN ? '0' : '') + hours;
    const minString = (minutes < TEN ? '0' : '') + minutes;
    const secString = (seconds < TEN ? '0' : '') + seconds;

    const dateString = `${hrsString}:${minString}:${secString}`;

    return dateString;
  }

  abbreviatedCurrency(value: number): string {
    return `${this.currencySymbol()}${this.abbreviatedNumber(value)}`;
  }
}

const INTL = new Internationalization();

const PSEUDO_LOCALIZATION_KEY = 'SKZ_ENABLE_PSEUDO_LOCALIZATION';

// Don't delete this as it is used for Xcode strings during I18n export

(() => {
  if (Platform.OS !== 'ios') {
    return;
  }
  const enablePSL = () => {
    const flag = Preferences.get(PSEUDO_LOCALIZATION_KEY) || false;
    INTL.enablePsueodLocalization(flag);
  };
  enablePSL();

  Preferences.watchKeys(PSEUDO_LOCALIZATION_KEY, (): void => enablePSL());
})();

const extraStrings = [];

// this type allows for autocompletion of existing translations, but also allow any string
function _m(key: keyof typeof englishTranslations | ({} & string), ...str: any[]): string {
  return INTL.message(key, ...str);
}

export {_m, INTL as default};
